import React, { useMemo } from "react";
import classNames from "classnames";
import { CellContext } from "@tanstack/react-table";
import { RowExpansion } from "@design-system/ViewTable/RowExpansion";
import type { ViewTableFeatures } from "@design-system/ViewTable/core/table";
import { getShortJournalEntryReferenceNumber } from "@common/helpers/accounting";
import { fullDateLongFormat } from "@common/helpers/date";
import { AccountingLedgerBasis } from "@common/types/domains/accounting/ledger";
import { ArAgingDetailSource } from "@common/types/reports/ar-aging";
import { formatDateOnly } from "@helpers/date";
import { ViewTableCell } from "@design-system/ViewTable/ViewTableCell";
import { useEntitiesById } from "@hooks/useEntities";
import { useOverlays } from "contexts/OverlayContext";
import { useClasses } from "@api/class.api";
import { ClassAvatarGroup } from "@components/avatar/ClassAvatarGroup";
import { useReportAmountDisplay } from "../useReportAmountDisplay";
import { ArAgingDetailViewRow } from "./arAgingDetailViewTypes";
import styles from "./ArAgingDetailTable.module.scss";

type ArAgingDetailCellContext = CellContext<
    ViewTableFeatures,
    ArAgingDetailViewRow,
    unknown
>;

// Leading pinned column: the bucket label on bucket rows, "Total" on
// bucket-total and grand-total rows, and the invoice date on line rows
// (mirrors the GL / EliminationDetail "Account / Date" convention).
export const ArAgingDetailNameCell = React.memo(function ArAgingDetailNameCell({
    row,
}: ArAgingDetailCellContext): React.ReactElement {
    const { rowKind, bucketLabel, date } = row.original;

    if (rowKind === "bucket") {
        return (
            <ViewTableCell className={styles.bucketCell}>
                {bucketLabel}
            </ViewTableCell>
        );
    }
    if (rowKind === "bucketTotal" || rowKind === "grandTotal") {
        return (
            <ViewTableCell
                className={
                    rowKind === "grandTotal"
                        ? styles.grandTotalLabelCell
                        : styles.bucketTotalCell
                }
            >
                Total
            </ViewTableCell>
        );
    }
    if (date) {
        return (
            <ViewTableCell>
                {formatDateOnly(date, fullDateLongFormat)}
            </ViewTableCell>
        );
    }
    return <ViewTableCell />;
});

export const ArAgingDetailSourceCell = React.memo(
    function ArAgingDetailSourceCell({
        row,
    }: ArAgingDetailCellContext): React.ReactElement {
        const { openOverlay } = useOverlays();
        const { rowKind, source, invoiceId, entityId } = row.original;

        if (rowKind !== "line") {
            return <ViewTableCell />;
        }

        const label =
            source === ArAgingDetailSource.INVOICE ? "Invoice" : "QuickBooks";
        if (!invoiceId || entityId === undefined) {
            return <ViewTableCell>{label}</ViewTableCell>;
        }

        return (
            <ViewTableCell
                captureClicks
                className={styles.clickableCell}
                onClick={() =>
                    openOverlay("invoiceLineItemsDrilldown", {
                        invoiceId,
                        entityIds: [entityId],
                    })
                }
            >
                {label}
            </ViewTableCell>
        );
    },
);

export const ArAgingDetailReferenceCell = React.memo(
    function ArAgingDetailReferenceCell({
        row,
    }: ArAgingDetailCellContext): React.ReactElement {
        const { rowKind, reference, entityId } = row.original;
        const { openOverlay } = useOverlays();
        const entitiesById = useEntitiesById();

        if (rowKind !== "line" || !reference) {
            return <ViewTableCell />;
        }

        const entity =
            entityId !== undefined ? entitiesById.get(entityId) : undefined;
        const displayReference = getShortJournalEntryReferenceNumber(reference);

        if (!entity) {
            return <ViewTableCell>{displayReference}</ViewTableCell>;
        }

        return (
            <ViewTableCell
                captureClicks
                className={styles.clickableCell}
                onClick={() =>
                    openOverlay("journalEntryDrawer", {
                        entity,
                        journalEntryId: reference,
                        ledgerBasis: AccountingLedgerBasis.accruals,
                    })
                }
            >
                {displayReference}
            </ViewTableCell>
        );
    },
);

export const ArAgingDetailCustomerCell = React.memo(
    function ArAgingDetailCustomerCell({
        row,
    }: ArAgingDetailCellContext): React.ReactElement {
        if (row.original.rowKind !== "line") {
            return <ViewTableCell />;
        }
        return <ViewTableCell>{row.original.customerName}</ViewTableCell>;
    },
);

export const ArAgingDetailDescriptionCell = React.memo(
    function ArAgingDetailDescriptionCell({
        row,
    }: ArAgingDetailCellContext): React.ReactElement {
        const { rowKind, description } = row.original;
        if (rowKind !== "line" || !description) {
            return <ViewTableCell />;
        }
        return <ViewTableCell>{description}</ViewTableCell>;
    },
);

export const ArAgingDetailClassCell = React.memo(
    function ArAgingDetailClassCell({
        row,
    }: ArAgingDetailCellContext): React.ReactElement {
        const { rowKind, classId } = row.original;
        const { classesMap } = useClasses();

        const classes = useMemo(() => {
            if (!classId) {
                return [];
            }
            const classEntity = classesMap.get(classId);
            return classEntity ? [classEntity] : [];
        }, [classId, classesMap]);

        if (rowKind !== "line" || classes.length === 0) {
            return <ViewTableCell />;
        }
        return (
            <ViewTableCell>
                <ClassAvatarGroup classes={classes} max={3} />
            </ViewTableCell>
        );
    },
);

export const ArAgingDetailEntityCell = React.memo(
    function ArAgingDetailEntityCell({
        row,
    }: ArAgingDetailCellContext): React.ReactElement {
        const { rowKind, entityName } = row.original;
        if (rowKind !== "line" || !entityName) {
            return <ViewTableCell />;
        }
        return <ViewTableCell>{entityName}</ViewTableCell>;
    },
);

export const ArAgingDetailDueDateCell = React.memo(
    function ArAgingDetailDueDateCell({
        row,
    }: ArAgingDetailCellContext): React.ReactElement {
        const { rowKind, dueDate } = row.original;
        if (rowKind !== "line") {
            return <ViewTableCell />;
        }
        return (
            <ViewTableCell>
                {formatDateOnly(dueDate, fullDateLongFormat)}
            </ViewTableCell>
        );
    },
);

// Original invoice line amount \u2014 secondary column; the Open column carries
// the primary/prominent right-pinned value.
export const ArAgingDetailAmountCell = React.memo(
    function ArAgingDetailAmountCell({
        row,
    }: ArAgingDetailCellContext): React.ReactElement {
        const { rowKind, amount } = row.original;
        const { text, negativeClassName } = useReportAmountDisplay(amount ?? 0);

        if (rowKind !== "line") {
            return <ViewTableCell />;
        }

        return (
            <ViewTableCell
                className={classNames(styles.numericCell, negativeClassName)}
            >
                {text}
            </ViewTableCell>
        );
    },
);

// The outstanding-as-of-date amount \u2014 the report's primary right-pinned
// column. Bucket rows show their subtotalOpen when collapsed (hidden when
// expanded); bucket-total and grand-total rows always show subtotalOpen;
// line rows show their own `open` value.
export const ArAgingDetailOpenCell = function ArAgingDetailOpenCell({
    row,
}: ArAgingDetailCellContext): React.ReactElement {
    const { rowKind, open, subtotalOpen } = row.original;

    let value = 0;
    if (rowKind === "line") {
        value = open ?? 0;
    } else if (
        rowKind === "bucket" ||
        rowKind === "bucketTotal" ||
        rowKind === "grandTotal"
    ) {
        value = subtotalOpen ?? 0;
    }
    const { text, negativeClassName } = useReportAmountDisplay(value);

    if (rowKind === "bucket") {
        return (
            <RowExpansion row={row}>
                {(isExpanded) =>
                    isExpanded ? (
                        <ViewTableCell />
                    ) : (
                        <ViewTableCell
                            className={classNames(
                                styles.numericCell,
                                styles.bucketCell,
                                negativeClassName,
                            )}
                        >
                            {text}
                        </ViewTableCell>
                    )
                }
            </RowExpansion>
        );
    }
    if (rowKind === "bucketTotal" || rowKind === "grandTotal") {
        return (
            <ViewTableCell
                className={classNames(
                    styles.numericCell,
                    rowKind === "grandTotal"
                        ? styles.grandTotalLabelCell
                        : styles.bucketTotalCell,
                    negativeClassName,
                )}
            >
                {text}
            </ViewTableCell>
        );
    }
    return (
        <ViewTableCell
            className={classNames(styles.numericCell, negativeClassName)}
        >
            {text}
        </ViewTableCell>
    );
};
